import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { AlertTriangle, Crown, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";

interface PlanInfo {
  plan: {
    name: string;
    price: number;
    currency: string;
    period: string;
  };
  limits: {
    clients: { used: number; limit: number | null };
    invoices: { used: number; limit: number | null };
    tasks: { used: number; limit: number | null };
    team_members: { used: number; limit: number | null };
  };
  subscription: {
    status: string;
    trialDaysLeft?: number;
  };
}

export function PlanInfoWidget() {
  const [, setLocation] = useLocation();
  const { t } = useTranslation(['common', 'plans']);
  
  const { data: planInfoRaw, isLoading, error } = useQuery<any>({
    queryKey: ["/api/plan-info"],
    refetchInterval: 30000, // Refresh every 30 seconds
    retry: 1,
    staleTime: 60000, // 1 minute cache
    queryFn: async () => {
      const response = await fetch('/api/plan-info', {
        credentials: 'include',
        headers: { 'Accept': 'application/json' },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    },
  });

  // Map server response to component interface
  // Server returns: { plan, limits: { maxClients, maxInvoices, maxTasks, maxTeamMembers }, usage: { clients, invoices, tasks, teamMembers }, ... }
  const planInfo: PlanInfo | undefined = planInfoRaw ? {
    plan: {
      name: planInfoRaw.plan || planInfoRaw.currentPlan || 'FREE',
      price: planInfoRaw.price || 0,
      currency: planInfoRaw.currency || 'EUR',
      period: planInfoRaw.period || 'month',
    },
    limits: {
      clients: {
        used: planInfoRaw.usage?.clients || 0,
        limit: (planInfoRaw.limits?.maxClients !== undefined && planInfoRaw.limits.maxClients !== -1) ? planInfoRaw.limits.maxClients : null,
      },
      invoices: {
        used: planInfoRaw.usage?.invoices || 0,
        limit: (planInfoRaw.limits?.maxInvoices !== undefined && planInfoRaw.limits.maxInvoices !== -1) ? planInfoRaw.limits.maxInvoices : null,
      },
      tasks: {
        used: planInfoRaw.usage?.tasks || 0,
        limit: (planInfoRaw.limits?.maxTasks !== undefined && planInfoRaw.limits.maxTasks !== -1) ? planInfoRaw.limits.maxTasks : null,
      },
      team_members: {
        used: planInfoRaw.usage?.teamMembers || 0,
        limit: (planInfoRaw.limits?.maxTeamMembers !== undefined && planInfoRaw.limits.maxTeamMembers !== -1) ? planInfoRaw.limits.maxTeamMembers : null,
      },
    },
    subscription: {
      status: planInfoRaw.isActive ? 'active' : 'inactive',
      trialDaysLeft: planInfoRaw.remainingDays || planInfoRaw.trialDaysLeft,
    },
  } : undefined;

  // Show loading state or error gracefully
  if (isLoading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Crown className="w-5 h-5 text-primary animate-pulse" />
            <span className="animate-pulse">{t('plans:loading') || 'Načítavam...'}</span>
          </CardTitle>
        </CardHeader>
      </Card>
    );
  }

  if (!planInfo || error) {
    return null; // Don't show error, just hide the widget
  }

  const { plan, limits, subscription } = planInfo;
  
  // Calculate usage percentages with safe access
  const clientsPercentage = (limits?.clients?.limit && limits?.clients?.used) ? (limits.clients.used / limits.clients.limit) * 100 : 0;
  const tasksPercentage = (limits?.tasks?.limit && limits?.tasks?.used) ? (limits.tasks.used / limits.tasks.limit) * 100 : 0;
  const invoicesPercentage = (limits?.invoices?.limit && limits?.invoices?.used) ? (limits.invoices.used / limits.invoices.limit) * 100 : 0;
  
  // Check if approaching limits
  const isApproachingLimits = [clientsPercentage, tasksPercentage, invoicesPercentage].some(p => p > 80);
  const isAtLimits = [clientsPercentage, tasksPercentage, invoicesPercentage].some(p => p >= 100);

  return (
    <Card className="w-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Crown className="w-5 h-5 text-primary" />
            {plan?.name ? t(`plans:${plan.name.toLowerCase()}`) || plan.name : (t('plans:unknown') || 'Neznámy plán')}
          </CardTitle>
          
          <div className="flex gap-2">
            {subscription?.trialDaysLeft !== undefined && subscription?.trialDaysLeft > 0 && (
              <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                {subscription.trialDaysLeft} {t('plans:trialDays', { count: subscription.trialDaysLeft }) || 'dní skúšobne'}
              </Badge>
            )}
            
            {isAtLimits && (
              <Badge variant="destructive" className="flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {t('plans:limitReached') || 'Limit dosiahnutý'}
              </Badge>
            )}
            
            {isApproachingLimits && !isAtLimits && (
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                {t('plans:approachingLimit') || 'Blížite sa k limitu'}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Usage overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="space-y-1">
            <div className="text-sm text-muted-foreground">Klienti</div>
            <div className="font-medium">
              {limits?.clients?.used || 0} / {limits?.clients?.limit || '∞'}
            </div>
            {limits?.clients?.limit && (
              <Progress value={clientsPercentage} className="h-1" />
            )}
          </div>
          
          <div className="space-y-1">
            <div className="text-sm text-muted-foreground">Úlohy</div>
            <div className="font-medium">
              {limits?.tasks?.used || 0} / {limits?.tasks?.limit || '∞'}
            </div>
            {limits?.tasks?.limit && (
              <Progress value={tasksPercentage} className="h-1" />
            )}
          </div>
          
          <div className="space-y-1">
            <div className="text-sm text-muted-foreground">Faktúry</div>
            <div className="font-medium">
              {limits?.invoices?.used || 0} / {limits?.invoices?.limit || '∞'}
            </div>
            {limits?.invoices?.limit && (
              <Progress value={invoicesPercentage} className="h-1" />
            )}
          </div>
          
          <div className="space-y-1">
            <div className="text-sm text-muted-foreground">Tím</div>
            <div className="font-medium">
              {limits?.team_members?.used || 0} / {limits?.team_members?.limit || '∞'}
            </div>
          </div>
        </div>

        {/* Upgrade prompt if approaching limits or on free plan */}
        {(isApproachingLimits || plan?.name?.toLowerCase().includes('free') || subscription?.trialDaysLeft !== undefined) && (
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="space-y-1">
              <div className="font-medium text-sm">
                {isAtLimits ? (t('plans:upgradeToHigherPlan') || 'Prejdite na vyšší plán') : (t('plans:considerUpgrade') || 'Zvážte upgrade')}
              </div>
              <div className="text-xs text-muted-foreground">
                {isAtLimits 
                  ? (t('plans:reachedPlanLimits') || 'Dosiahli ste limity vášho plánu')
                  : (t('plans:getMoreSpace') || 'Získajte viac priestoru pre rast')
                }
              </div>
            </div>
            
            <Button 
              size="sm" 
              variant={isAtLimits ? "default" : "outline"}
              onClick={() => setLocation('/profile?tab=subscription')}
              className="flex items-center gap-2"
            >
              <TrendingUp className="w-3 h-3" />
              {t('plans:upgrade') || 'Upgrade'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}